<script setup>
import { computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';
import BaseBubble from './Base.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';

const { attachments, inboxId, sender } = useMessageContext();

const $store = useStore();
const { t } = useI18n();

const attachment = computed(() => {
  return attachments.value[0];
});

const phoneNumber = computed(() => {
  return attachment.value?.fallbackTitle || '';
});

const contactName = computed(() => {
  const { meta } = attachment.value ?? {};
  const { firstName, lastName } = meta ?? {};
  return `${firstName ?? ''} ${lastName ?? ''}`.trim();
});

const rawPhoneNumber = computed(() => {
  return phoneNumber.value.replace(/\D/gu, '');
});

const sharedBy = computed(() => {
  const senderName = sender.value?.name;
  if (!senderName) return '';

  return t('CONVERSATION.SHARED_ATTACHMENT.CONTACT', {
    sender: senderName,
  });
});

function getContactObject() {
  return {
    name: contactName.value || phoneNumber.value,
    phone_number: `+${rawPhoneNumber.value}`,
  };
}

async function filterContactByNumber(searchCandidate) {
  const query = {
    attribute_key: 'phone_number',
    filter_operator: 'equal_to',
    values: [searchCandidate],
    attribute_model: 'standard',
    custom_attribute_type: '',
  };

  const queryPayload = { payload: [query] };
  const contacts = await $store.dispatch('contacts/filter', {
    queryPayload,
    resetState: false,
  });
  return contacts.shift();
}

function openContactNewTab(contactId) {
  const accountId = window.location.pathname.split('/')[3];
  const url = `/app/accounts/${accountId}/contacts/${contactId}`;
  window.open(url, '_blank');
}

async function addContact() {
  try {
    let contact = await filterContactByNumber(rawPhoneNumber.value);
    if (!contact) {
      contact = await $store.dispatch('contacts/create', getContactObject());
      useAlert(t('CONTACT_FORM.SUCCESS_MESSAGE'));
    }
    openContactNewTab(contact.id);
  } catch (error) {
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('phone_number')) {
        useAlert(t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t('CONTACT_FORM.ERROR_MESSAGE'));
    }
  }
}
</script>

<template>
  <BaseBubble class="overflow-hidden p-3" data-bubble-name="contact">
    <div class="grid min-w-64 gap-4">
      <div class="grid gap-3">
        <div class="grid size-8 place-content-center rounded-lg bg-[#D6409F]">
          <span class="i-lucide-contact-round size-4 text-white" />
        </div>
        <div class="space-y-1 overflow-hidden">
          <div v-if="sharedBy" class="truncate text-sm text-n-slate-12">
            {{ sharedBy }}
          </div>
          <div v-if="contactName" class="truncate text-sm text-n-slate-12">
            {{ contactName }}
          </div>
          <div v-if="phoneNumber" class="truncate text-sm text-n-slate-11">
            {{ phoneNumber }}
          </div>
        </div>
      </div>

      <div v-if="rawPhoneNumber" class="grid grid-cols-2 gap-2">
        <ComposeConversation
          :initial-phone="phoneNumber"
          :initial-name="contactName"
          :initial-inbox-id="inboxId"
          align="start"
        >
          <template #trigger="{ isOpen }">
            <NextButton
              class="w-full skip-context-menu"
              label="Enviar mensagem"
              icon="i-lucide-message-circle"
              color="teal"
              size="sm"
              :aria-expanded="isOpen"
            />
          </template>
        </ComposeConversation>

        <NextButton
          class="w-full skip-context-menu"
          label="Salvar contato"
          icon="i-lucide-user-plus"
          color="slate"
          variant="outline"
          size="sm"
          @click.stop="addContact"
        />
      </div>
    </div>
  </BaseBubble>
</template>
